import {
  type AppGetInput,
  type AppGetSuccess,
  type AppId,
  type CompanyData,
  type CompanyGetInput,
  type CompanyGetSuccess,
  type CompanyId,
  type GameGetInput,
  type GameGetSuccess,
  type GameId,
  isDefined,
  MINUTE,
  type ResponseSuccess,
  type TeamGetInput,
  type TeamGetSuccess,
  type TeamId,
  type UserId,
  type UserProfileInput,
  type UserProfileSuccess,
} from '@terros/common'
import { useTantalimQuery } from './query'
import { uploadUrlsKey } from './keys'
import { type GetHookResponse } from './hooks'

export type AvatarUploadUrl = { uploadAvatarUrl?: string }
export type CompanyUploadUrls = Pick<CompanyData, 'uploadAvatarUrl' | 'uploadIconUrl'>
export type CoverUploadUrl = { coverUploadUrl?: string }

// Upload URLs are write credentials that expire in about an hour, so only upload controls ask for them. They are
// cached apart from the entity's own read, which never asks, and refreshed well before they expire: staleTime alone
// only refetches on mount or focus, so the interval keeps a control that stays mounted and focused from holding an
// expired URL. The API returns none when the caller may not upload, including when the field is locked, so a missing
// URL means "can't upload".
const UPLOAD_URL_STALE_TIME = 15 * MINUTE

type UploadUrlQuery<Input, Success, Data> = {
  url: string
  entityId: string | undefined
  input: Input
  enabled: boolean
  convertResponse: (success: Success) => Data
}

function useUploadUrlQuery<Input extends { includeUploadUrls?: boolean }, Success extends ResponseSuccess, Data>({
  url,
  entityId,
  input,
  enabled,
  convertResponse,
}: UploadUrlQuery<Input, Success, Data>): GetHookResponse<Data> {
  return useTantalimQuery<Input, Success, Data>({
    url,
    queryKey: uploadUrlsKey(url, entityId),
    input: { ...input, includeUploadUrls: true },
    convertResponse,
    options: {
      enabled,
      staleTime: UPLOAD_URL_STALE_TIME,
      gcTime: UPLOAD_URL_STALE_TIME,
      refetchInterval: UPLOAD_URL_STALE_TIME,
    },
  })
}

/** The avatar upload URL for a user, or for the current user when `userId` is undefined. */
export function useUserAvatarUploadUrl(userId: UserId | undefined, enabled = true): GetHookResponse<AvatarUploadUrl> {
  return useUploadUrlQuery<UserProfileInput, UserProfileSuccess, AvatarUploadUrl>({
    url: '/user/profile',
    entityId: userId,
    input: { userId },
    enabled,
    convertResponse: ({ user }) => ({ uploadAvatarUrl: user.uploadAvatarUrl }),
  })
}

export function useTeamAvatarUploadUrl(teamId: TeamId | undefined, enabled = true): GetHookResponse<AvatarUploadUrl> {
  return useUploadUrlQuery<TeamGetInput, TeamGetSuccess, AvatarUploadUrl>({
    url: '/team/get',
    entityId: teamId,
    input: { teamId: teamId ?? 'Team:EMPTY_TEAM' },
    enabled: enabled && isDefined(teamId),
    convertResponse: ({ team }) => ({ uploadAvatarUrl: team.uploadAvatarUrl }),
  })
}

export function useCompanyUploadUrls(
  companyId: CompanyId | undefined,
  enabled = true
): GetHookResponse<CompanyUploadUrls> {
  return useUploadUrlQuery<CompanyGetInput, CompanyGetSuccess, CompanyUploadUrls>({
    url: '/company/get',
    entityId: companyId,
    input: { companyId },
    enabled: enabled && isDefined(companyId),
    convertResponse: ({ company }) => ({
      uploadAvatarUrl: company.uploadAvatarUrl,
      uploadIconUrl: company.uploadIconUrl,
    }),
  })
}

export function useConnectAppAvatarUploadUrl(appId: AppId, enabled = true): GetHookResponse<AvatarUploadUrl> {
  return useUploadUrlQuery<AppGetInput, AppGetSuccess, AvatarUploadUrl>({
    url: '/connect/app/get',
    entityId: appId,
    input: { appId },
    enabled,
    convertResponse: ({ app }) => ({ uploadAvatarUrl: app.uploadAvatarUrl }),
  })
}

export function useGameCoverUploadUrl(gameId: GameId, enabled = true): GetHookResponse<CoverUploadUrl> {
  return useUploadUrlQuery<GameGetInput, GameGetSuccess, CoverUploadUrl>({
    url: '/game/get',
    entityId: gameId,
    input: { gameId },
    enabled,
    convertResponse: ({ game }) => ({ coverUploadUrl: game.cover?.upload }),
  })
}
